import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'

import { OfferModel } from '@/types'

import { CampaignProductPicker, CampaignVariationPicker } from '../CampaignProductPicker'

interface Props {
  rules: Partial<OfferModel.ComboFixedPriceRules>
  onChange: (rules: Partial<OfferModel.ComboFixedPriceRules>) => void
}

export const ComboFixedPriceEditor = ({ rules, onChange }: Props) => {
  const items = rules.items ?? []

  const handleProductChange = (ids: string[]) => {
    const existing = items.filter(item => ids.includes(item.product_id))
    const added = ids
      .filter(id => !existing.some(item => item.product_id === id))
      .map(id => ({ product_id: id, product_variation_id: '', quantity: 1 }))
    onChange({ ...rules, items: [...existing, ...added] })
  }

  const updateItem = (
    product_id: string,
    patch: Partial<OfferModel.ComboFixedPriceRules['items'][0]>,
  ) =>
    onChange({
      ...rules,
      items: items.map(item => item.product_id === product_id ? { ...item, ...patch } : item),
    })

  return (
    <div className="space-y-4">
      <CampaignProductPicker
        label="Select bundle products"
        selectedIds={items.map(i => i.product_id)}
        onChange={handleProductChange}
        multiple
      />

      {items.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-ink">Bundle item details</p>
          {items.map(item => (
            <div key={item.product_id} className="grid grid-cols-1 gap-3 rounded-lg border border-border-subtle p-3 sm:grid-cols-2">
              <div className="truncate text-sm text-text-2 sm:col-span-2">
                Product: {item.product_id}
              </div>
              <div className="space-y-1.5">
                <Label>Variation</Label>
                <CampaignVariationPicker
                  productId={item.product_id}
                  value={item.product_variation_id}
                  onChange={(variationId) => updateItem(item.product_id, { product_variation_id: variationId })}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Quantity</Label>
                <Input
                  type="number"
                  min={1}
                  value={item.quantity}
                  onChange={(e) => updateItem(item.product_id, { quantity: Number(e.target.value) })}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="space-y-1.5">
        <Label>Bundle fixed price</Label>
        <Input
          type="number"
          min={0}
          value={rules.price ?? ''}
          onChange={(e) => onChange({ ...rules, price: Number(e.target.value) })}
          placeholder="0.00"
        />
      </div>

      <div className="flex items-center gap-3">
        <Switch
          checked={rules.free_delivery ?? false}
          onCheckedChange={(v) => onChange({ ...rules, free_delivery: v })}
        />
        <Label>Free delivery</Label>
      </div>
    </div>
  )
}
